import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParameterCodec, HttpParams } from '@angular/common/http';

type QueryParams = Record<string, string | number | boolean | null | undefined>;

/**
 * Angular's default HttpUrlEncodingCodec un-encodes commas, semicolons, etc.
 * This strict encoder uses encodeURIComponent for full RFC 3986 encoding,
 * ensuring characters like commas are sent as %2C (required by GTN API).
 */
class StrictHttpParamsEncoder implements HttpParameterCodec {
  encodeKey(key: string): string { return encodeURIComponent(key); }
  encodeValue(value: string): string { return encodeURIComponent(value); }
  decodeKey(key: string): string { return decodeURIComponent(key); }
  decodeValue(value: string): string { return decodeURIComponent(value); }
}

@Injectable({ providedIn: 'root' })
export class NetworkHelper {
  constructor(private http: HttpClient) {}

  static prepareHttpOptions(customToken?: string): HttpHeaders {
    const headerObj: Record<string, string> = {};

    if (customToken) {
      headerObj['Authorization'] = customToken;
    }

    return new HttpHeaders(headerObj);
  }

  get<T>(url: string, jsonData?: QueryParams, customToken?: string): Observable<T> {
    const headers = NetworkHelper.prepareHttpOptions(customToken);
    const params = NetworkHelper.buildParams(jsonData);
    return this.http.get<T>(url, { headers, params });
  }

  getText(url: string, jsonData?: QueryParams, customToken?: string): Observable<string> {
    const headers = NetworkHelper.prepareHttpOptions(customToken);
    const params = NetworkHelper.buildParams(jsonData);
    return this.http.get(url, { headers, params, responseType: 'text' });
  }

  getOut<T>(url: string, jsonData?: QueryParams): Observable<T> {
    const params = NetworkHelper.buildParams(jsonData);
    return this.http.get<T>(url, {
      params,
      headers: new HttpHeaders({}),
    });
  }

  post<T>(url: string, data: unknown, customToken?: string): Observable<T> {
    const headers = NetworkHelper.prepareHttpOptions(customToken);
    return this.http.post<T>(url, data, { headers });
  }

  put<T>(url: string, data: unknown, customToken?: string): Observable<T> {
    const headers = NetworkHelper.prepareHttpOptions(customToken);
    return this.http.put<T>(url, data, { headers });
  }

  patch<T>(url: string, data: unknown, customToken?: string): Observable<T> {
    const headers = NetworkHelper.prepareHttpOptions(customToken);
    return this.http.patch<T>(url, data, { headers });
  }

  delete<T>(url: string, jsonData?: QueryParams, customToken?: string): Observable<T> {
    const headers = NetworkHelper.prepareHttpOptions(customToken);
    const params = NetworkHelper.buildParams(jsonData);
    return this.http.delete<T>(url, { headers, params });
  }

  private static buildParams(jsonData?: QueryParams): HttpParams {
    let params = new HttpParams({ encoder: new StrictHttpParamsEncoder() });
    if (jsonData != null) {
      for (const key of Object.keys(jsonData)) {
        const value = jsonData[key];
        if (value != null) {
          params = params.set(key, value);
        }
      }
    }
    return params;
  }
}
